"use client";

import { useEffect, useMemo, useRef, useState, type ComponentType, type SVGProps } from "react";
import { ArrowRight, FileArchive, FileCode2, Frame, PackageCheck, Palette, Play, Radar } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { heroFileBadges, heroIntents, type HeroIntent } from "./homepage-data";
import { LandingDemoMenu } from "./LandingDemoMenu";
import { useAuth } from "@/components/auth/AuthProvider";

type IconType = ComponentType<SVGProps<SVGSVGElement>>;

type LandingHeroProps = {
  intent: HeroIntent;
  setIntent: (intent: HeroIntent) => void;
};

// Non-admin fallback: the original iframe pipeline loop, but click-to-activate.
// The three.js loop is a multi-MB payload, so above the fold we render a
// lightweight styled placeholder and only mount the iframe once the visitor
// opts in (click or pointer-enter). Zero heavy payload until intent.
function AssetPipelineVisual({ src, title }: { src: string; title: string }) {
  const [activated, setActivated] = useState(false);

  return (
    <div className="relative aspect-square w-full overflow-hidden rounded-[2rem] border border-[hsl(var(--lb-line))] bg-[#0a0807] shadow-[0_28px_95px_rgba(0,0,0,0.22)]">
      {activated ? (
        <iframe
          src={src}
          title={title}
          className="absolute inset-0 h-full w-full border-0"
          sandbox="allow-scripts allow-same-origin"
        />
      ) : (
        <button
          type="button"
          onClick={() => setActivated(true)}
          onPointerEnter={() => setActivated(true)}
          aria-label={`Play ${title}`}
          className="group absolute inset-0 flex flex-col items-center justify-center gap-5 bg-[radial-gradient(120%_120%_at_30%_20%,hsl(var(--lb-clay)/0.28),transparent_55%),radial-gradient(120%_120%_at_80%_90%,hsl(var(--lb-amber)/0.2),transparent_50%),linear-gradient(160deg,#151009,#0a0807)] transition"
        >
          {/* Play control */}
          <span className="relative grid h-16 w-16 place-items-center rounded-full border border-white/15 bg-white/10 backdrop-blur-sm transition group-hover:scale-105 group-hover:bg-white/15">
            <span className="absolute inline-flex h-full w-full rounded-full bg-[hsl(var(--lb-clay)/0.5)] opacity-60 motion-safe:animate-ping" />
            <Play className="relative ml-0.5 h-6 w-6 fill-white text-white" />
          </span>
          <span className="flex flex-col items-center gap-1.5">
            <span className="font-mono text-xs uppercase tracking-[0.22em] text-white/60">See it live</span>
            <span className="text-sm font-medium text-white/85">{title}</span>
          </span>
        </button>
      )}
    </div>
  );
}

// A small pulsing "live" indicator dot (clay) for the window chrome.
function LiveDot() {
  return (
    <span className="relative flex h-1.5 w-1.5">
      <span className="absolute inline-flex h-full w-full rounded-full bg-[hsl(var(--lb-clay))] opacity-70 motion-safe:animate-ping" />
      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[hsl(var(--lb-clay))]" />
    </span>
  );
}

// Admin hero centerpiece: the Paint Booth clip framed as a live browser window.
// The wide 1920x938 clip finally sits in a container built for its aspect ratio,
// and the chrome makes the "one browser tab" headline literal.
function HeroBrowserWindow() {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    // Honor reduced-motion: hold on the poster frame instead of autoplaying.
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => {
      if (reduce.matches) {
        el.pause();
      } else {
        // play() rejects when autoplay is blocked — leave the poster showing.
        void el.play().catch(() => {});
      }
    };
    apply();
    reduce.addEventListener("change", apply);
    return () => reduce.removeEventListener("change", apply);
  }, []);

  return (
    <figure className="relative m-0 w-full overflow-hidden rounded-2xl border border-[hsl(var(--lb-line))] bg-[#0a0807] shadow-[0_34px_90px_-38px_rgba(0,0,0,0.85)] ring-1 ring-inset ring-[hsl(var(--lb-ink)/0.04)]">
      {/* Window chrome — decorative; the video below carries the accessible label. */}
      <div
        aria-hidden="true"
        className="flex items-center gap-3 border-b border-[hsl(var(--lb-line)/0.7)] bg-gradient-to-b from-[hsl(var(--lb-panel))] to-[hsl(var(--lb-bg-soft))] px-3.5 py-2.5"
      >
        <span className="flex shrink-0 gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[hsl(var(--lb-clay))]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[hsl(var(--lb-line))]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[hsl(var(--lb-line))]" />
        </span>
        <span className="shrink-0 rounded-md border border-[hsl(var(--lb-line)/0.7)] bg-[hsl(var(--lb-ink)/0.06)] px-2.5 py-1 text-xs text-[hsl(var(--lb-ink))]">
          Paint Booth
        </span>
        <span className="hidden min-w-0 flex-1 truncate font-mono text-[0.72rem] text-[hsl(var(--lb-subtle))] sm:block">
          theworkshop.gg/paint-booth
        </span>
        <span className="ml-auto inline-flex shrink-0 items-center gap-2 whitespace-nowrap font-mono text-[0.66rem] uppercase tracking-[0.08em] text-[hsl(var(--lb-muted))] sm:ml-0">
          <LiveDot />
          LIVE · 60fps
        </span>
      </div>

      {/* Live viewport */}
      <div className="relative aspect-[1920/938] w-full">
        <video
          ref={videoRef}
          className="absolute inset-0 h-full w-full object-cover"
          poster="/paint-booth-firetruck-poster.jpg"
          src="/paint-booth-firetruck.mp4"
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          aria-label="Paint Booth demo: a Los Santos Fire Department livery is edited in Photoshop and previewed live on the 3D firetruck model."
        />
        <figcaption
          aria-hidden="true"
          className="pointer-events-none absolute bottom-3 left-3 inline-flex items-center gap-2 rounded-lg border border-[hsl(var(--lb-line))] bg-black/65 px-2.5 py-1.5 font-mono text-[0.7rem] text-[hsl(var(--lb-ink))] backdrop-blur-sm"
        >
          <span className="h-2 w-2 rounded-[3px] bg-[hsl(var(--lb-clay))]" />
          livery.psd
        </figcaption>
        <span
          aria-hidden="true"
          className="pointer-events-none absolute right-3 top-3 inline-flex items-center gap-2 rounded-lg border border-[hsl(var(--lb-line))] bg-black/65 px-2.5 py-1.5 font-mono text-[0.7rem] text-[hsl(var(--lb-ink))] backdrop-blur-sm"
        >
          <span className="h-2 w-2 rounded-[3px] bg-[hsl(var(--lb-amber))]" />
          3D preview ready
        </span>
      </div>
    </figure>
  );
}

function toolIcon(tool: string): IconType {
  if (tool === "Paint Booth") return Palette;
  if (tool === "UV Studio") return Frame;
  if (tool === "EasyMeta") return FileCode2;
  if (tool === "PackBench") return PackageCheck;
  return Radar;
}

function IntentFlow({ intent, setIntent }: { intent: HeroIntent; setIntent: (intent: HeroIntent) => void }) {
  const ToolIcon = useMemo(() => toolIcon(intent.tool), [intent.tool]);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3 sm:gap-y-2">
        <span className="font-mono text-xs uppercase tracking-[0.22em] text-[hsl(var(--lb-subtle))]">
          What are we making today?
        </span>
        <div className="-mx-1 flex flex-wrap gap-1.5">
          {heroIntents.map((item) => {
            const active = item.key === intent.key;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setIntent(item)}
                aria-pressed={active}
                className={cn(
                  "group inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm tracking-[-0.01em] transition",
                  active
                    ? "border-[hsl(var(--lb-clay))] bg-[hsl(var(--lb-clay)/0.18)] text-[hsl(var(--lb-ink))]"
                    : "border-[hsl(var(--lb-line))] bg-transparent text-[hsl(var(--lb-muted))] hover:border-[hsl(var(--lb-clay)/0.55)] hover:text-[hsl(var(--lb-ink))]",
                )}
              >
                <span
                  className={cn(
                    "h-1.5 w-1.5 rounded-full transition",
                    active ? "bg-[hsl(var(--lb-clay))]" : "bg-[hsl(var(--lb-line))] group-hover:bg-[hsl(var(--lb-clay)/0.6)]",
                  )}
                />
                {item.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-sm tracking-[-0.01em] text-[hsl(var(--lb-muted))]">
        <span className="inline-flex items-center gap-2 text-[hsl(var(--lb-ink))]">
          <ToolIcon className="h-4 w-4 text-[hsl(var(--lb-clay))]" />
          {intent.tool}
        </span>
        <ArrowRight className="h-3.5 w-3.5 opacity-50" />
        <span className="inline-flex items-center gap-2">
          <FileArchive className="h-4 w-4 text-[hsl(var(--lb-amber))]" />
          {intent.files[0]}
        </span>
        <ArrowRight className="h-3.5 w-3.5 opacity-50" />
        <span className="inline-flex items-center gap-2 text-[hsl(var(--lb-ink))]">
          <PackageCheck className="h-4 w-4" />
          {intent.output}
        </span>
      </div>
    </div>
  );
}

// Shared CTA row — identical treatment across both hero variants so the buttons
// stay consistent with the rest of the site. `primaryLabel` lets each variant
// set its own call to action.
function HeroCtaRow({ primaryLabel }: { primaryLabel: string }) {
  return (
    <>
      <Link
        to="/auth?mode=sign-up"
        className="inline-flex h-12 items-center justify-center rounded-xl bg-[hsl(var(--lb-clay))] px-6 text-base font-semibold text-white transition hover:-translate-y-0.5 sm:h-16 sm:px-9 sm:text-lg"
      >
        {primaryLabel}
        <ArrowRight className="ml-2 h-5 w-5" />
      </Link>
      <LandingDemoMenu
        label="Try a demo"
        className="h-12 rounded-xl border border-[hsl(var(--lb-ink)/0.2)] bg-[hsl(var(--lb-panel)/0.55)] px-5 text-base font-semibold text-[hsl(var(--lb-ink))] transition hover:border-[hsl(var(--lb-ink)/0.35)] hover:bg-[hsl(var(--lb-panel)/0.85)] sm:h-16 sm:px-8 sm:text-lg"
        leadingIcon={<Play className="mr-2 h-5 w-5" />}
      />
      <a
        href="https://discord.gg/theworkshop"
        target="_blank"
        rel="noreferrer"
        className="inline-flex h-12 items-center justify-center whitespace-nowrap rounded-xl border border-[hsl(var(--lb-ink)/0.2)] bg-[hsl(var(--lb-panel)/0.55)] px-5 text-base font-semibold text-[hsl(var(--lb-ink))] transition hover:border-[hsl(var(--lb-ink)/0.35)] hover:bg-[hsl(var(--lb-panel)/0.85)] sm:h-16 sm:px-8 sm:text-lg"
      >
        <svg className="mr-2 h-5 w-5" viewBox="0 0 127.14 96.36" fill="currentColor" aria-hidden="true">
          <path d="M107.7 8.07A105.15 105.15 0 0 0 81.47 0a72.06 72.06 0 0 0-3.36 6.83 97.68 97.68 0 0 0-29.11 0A72.37 72.37 0 0 0 45.64 0a105.89 105.89 0 0 0-26.25 8.09C2.79 32.65-1.71 56.6.54 80.21a105.73 105.73 0 0 0 32.17 16.15 77.7 77.7 0 0 0 6.89-11.11 68.42 68.42 0 0 1-10.85-5.18c.91-.66 1.8-1.34 2.66-2a75.57 75.57 0 0 0 64.32 0c.87.71 1.76 1.39 2.66 2a68.68 68.68 0 0 1-10.87 5.19 77 77 0 0 0 6.89 11.1 105.25 105.25 0 0 0 32.19-16.14c2.64-27.38-4.51-51.11-18.9-72.15ZM42.45 65.69C36.18 65.69 31 60 31 53s5-12.74 11.43-12.74S54 46 53.89 53s-5.05 12.69-11.44 12.69Zm42.24 0C78.41 65.69 73.25 60 73.25 53s5-12.74 11.44-12.74S96.23 46 96.12 53s-5.04 12.69-11.43 12.69Z" />
        </svg>
        <span className="sm:hidden">Discord</span>
        <span className="hidden sm:inline">Join Our Discord</span>
      </a>
    </>
  );
}

// Admin-only redesign ("Cockpit"): punchier copy + the Paint Booth clip framed
// as a live browser window. Gated in LandingHero so the public landing is untouched.
function AdminLandingHero() {
  return (
    <section className="relative overflow-x-hidden overflow-y-visible pb-8 pt-6 sm:pb-12 sm:pt-8 lg:pb-14">
      {/* Animated background glow orbs */}
      <div className="animate-glow-drift absolute left-[78%] top-[16%] h-[38rem] w-[38rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[hsl(var(--lb-clay)/0.14)] blur-[90px]" />
      <div className="animate-glow-drift-slow absolute left-[16%] top-[2%] h-[28rem] w-[28rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[hsl(var(--lb-amber)/0.09)] blur-[70px]" />

      <div className="lb-container relative z-10 grid gap-10 pt-2 sm:pt-4 lg:grid-cols-[1fr_1.02fr] lg:items-center">
        <div className="max-w-[46rem]">
          <h1
            className="lb-display landing-reveal text-[2.4rem] font-semibold leading-[1.04] text-[hsl(var(--lb-ink))] [text-wrap:balance] sm:text-[4rem] sm:leading-[0.95] lg:text-[4.5rem] xl:text-[5.25rem]"
            style={{ animationDelay: "0ms" }}
          >
            Ship every FiveM asset from one browser tab.
          </h1>
          <p
            className="landing-reveal mt-5 max-w-[42rem] text-base leading-[1.4] tracking-[-0.02em] text-[hsl(var(--lb-muted))] sm:mt-7 sm:text-[1.4rem] sm:leading-[1.35] sm:tracking-[-0.025em]"
            style={{ animationDelay: "110ms" }}
          >
            Retexture liveries, generate meta, scan resources, and package streaming-ready builds — one connected workspace. Zero installs, zero tool-switching.
          </p>
          <p
            className="landing-reveal mt-4 text-base tracking-[-0.02em] text-[hsl(var(--lb-subtle))] sm:text-lg"
            style={{ animationDelay: "200ms" }}
          >
            Free to start — no card required.
          </p>
          <div
            className="landing-reveal mt-6 flex flex-col gap-3 sm:mt-8 sm:flex-row sm:flex-wrap sm:items-center sm:gap-5 [&_a]:whitespace-nowrap [&_button]:whitespace-nowrap"
            style={{ animationDelay: "300ms" }}
          >
            <HeroCtaRow primaryLabel="Start building — free" />
          </div>
          <div
            className="landing-reveal mt-8 flex flex-wrap gap-3 sm:mt-10"
            style={{ animationDelay: "420ms" }}
          >
            {heroFileBadges.map((badge) => (
              <span
                key={badge}
                className="inline-flex items-center rounded-full border border-[hsl(var(--lb-clay)/0.6)] bg-[hsl(var(--lb-bg-soft))] px-3.5 py-1.5 font-mono text-xs font-semibold uppercase tracking-[0.08em] text-[hsl(var(--lb-ink))] shadow-[inset_0_1px_0_hsl(var(--lb-ink)/0.06),0_6px_18px_hsl(0_0%_0%/0.12)]"
              >
                {badge}
              </span>
            ))}
          </div>
        </div>

        <div className="relative">
          <div className="landing-reveal" style={{ animationDelay: "240ms" }}>
            <HeroBrowserWindow />
          </div>
        </div>
      </div>
    </section>
  );
}

// Public landing hero — unchanged behavior for signed-out visitors and subscribers.
function DefaultLandingHero({ intent, setIntent }: LandingHeroProps) {
  return (
    <section className="relative overflow-x-hidden overflow-y-visible pb-8 pt-6 sm:pb-12 sm:pt-8 lg:pb-14">
      {/* Animated background glow orbs */}
      <div className="animate-glow-drift absolute left-[76%] top-[14%] h-[38rem] w-[38rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[hsl(var(--lb-clay)/0.13)] blur-[90px]" />
      <div className="animate-glow-drift-slow absolute left-[18%] top-[0%] h-[28rem] w-[28rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[hsl(var(--lb-amber)/0.09)] blur-[70px]" />

      <div className="lb-container relative z-10 grid gap-10 pt-2 sm:pt-4 lg:grid-cols-[1.05fr_0.78fr] lg:items-center">
        <div className="max-w-[92rem]">
          <h1
            className="lb-display landing-reveal max-w-[76rem] text-[2.5rem] font-semibold leading-[1.05] text-[hsl(var(--lb-ink))] sm:text-[6rem] sm:leading-[0.92] lg:text-[6.6rem] xl:text-[7.6rem]"
            style={{ animationDelay: "0ms" }}
          >
            Ship FiveM assets without leaving the browser.
          </h1>
          <p
            className="landing-reveal mt-5 max-w-[56rem] text-base leading-[1.4] tracking-[-0.025em] text-[hsl(var(--lb-muted))] sm:mt-8 sm:text-[1.65rem] sm:leading-[1.35] sm:tracking-[-0.035em]"
            style={{ animationDelay: "110ms" }}
          >
            Preview liveries, generate meta, scan resources, and package builds — all from one connected workspace. No local setup. No tool-switching.
          </p>
          <p
            className="landing-reveal mt-4 text-base tracking-[-0.02em] text-[hsl(var(--lb-subtle))] sm:text-lg"
            style={{ animationDelay: "200ms" }}
          >
            Free to start — no card required.
          </p>
          <div
            className="landing-reveal mt-6 flex flex-col gap-3 sm:mt-8 sm:flex-row sm:items-center sm:gap-5"
            style={{ animationDelay: "300ms" }}
          >
            <HeroCtaRow primaryLabel="Get started for free" />
          </div>
          <div
            className="landing-reveal mt-8 flex flex-wrap gap-3 sm:mt-10"
            style={{ animationDelay: "420ms" }}
          >
            {heroFileBadges.map((badge) => (
              <span
                key={badge}
                className="inline-flex items-center rounded-full border border-[hsl(var(--lb-clay)/0.6)] bg-[hsl(var(--lb-bg-soft))] px-3.5 py-1.5 font-mono text-xs font-semibold uppercase tracking-[0.08em] text-[hsl(var(--lb-ink))] shadow-[inset_0_1px_0_hsl(var(--lb-ink)/0.06),0_6px_18px_hsl(0_0%_0%/0.12)]"
              >
                {badge}
              </span>
            ))}
          </div>

          <div className="landing-reveal mt-10" style={{ animationDelay: "500ms" }}>
            <IntentFlow intent={intent} setIntent={setIntent} />
          </div>
        </div>

        <div className="relative">
          <div className="landing-reveal" style={{ animationDelay: "240ms" }}>
            <AssetPipelineVisual
              src={intent.loopSrc ?? "/workshop-loop.html"}
              title={`${intent.tool} hero loop`}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

export function LandingHero({ intent, setIntent }: LandingHeroProps) {
  const { isAdmin } = useAuth();

  if (isAdmin) {
    return <AdminLandingHero />;
  }

  return <DefaultLandingHero intent={intent} setIntent={setIntent} />;
}
